import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getDeliveryProfiles, deleteDeliveryProfile } from '../../actions/deliveryProfile';
import { setAlert } from '../../actions/alert';
import { Plus, Search, Edit, Trash2, Truck, MapPin, Package, AlertTriangle } from 'lucide-react';

const DeliveryProfiles = ({ getDeliveryProfiles, deleteDeliveryProfile, setAlert, deliveryProfiles, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [profileToDelete, setProfileToDelete] = useState(null);
    const [filteredProfiles, setFilteredProfiles] = useState([]);

    useEffect(() => {
        document.title = 'Delivery Profiles';
        getDeliveryProfiles();
    }, []);

    useEffect(() => {
        if (deliveryProfiles) {
            const filtered = deliveryProfiles.filter(profile =>
                profile.productProfile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                profile.serviceableArea?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                profile.superServiceableArea?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                profile._id?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredProfiles(filtered);
        }
    }, [deliveryProfiles, searchTerm]);

    const handleDeleteClick = (profile) => {
        setProfileToDelete(profile);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (profileToDelete) {
            deleteDeliveryProfile(profileToDelete._id);
            setShowDeleteModal(false);
            setProfileToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setProfileToDelete(null);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <Truck size={32} className="me-3 text-primary" />
                                    Delivery Profiles
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage delivery profiles linking products with serviceable areas
                                </p>
                            </div>
                            <Link 
                                to="/inventory/v1.2/delivery-profiles/new"
                                className="btn btn-primary btn-lg shadow-sm d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Create New Profile
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Search and Stats Section */}
                <div className="row mb-4">
                    <div className="col-lg-6">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search by product profile, serviceable area, or ID..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card shadow-sm bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{deliveryProfiles?.length || 0}</div>
                                        <div className="small">Total Profiles</div>
                                    </div>
                                    <Truck size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Delivery Profiles Grid */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading delivery profiles...</p>
                    </div>
                ) : filteredProfiles && filteredProfiles.length > 0 ? (
                    <div className="row">
                        {filteredProfiles.map((profile) => (
                            <div key={profile._id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                                <div 
                                    className="card shadow-sm border-0 h-100" 
                                    style={{
                                        borderRadius: '0.75rem',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '';
                                    }}
                                >
                                    <div className="card-header bg-white border-bottom-0 pb-0">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title mb-1 text-primary fw-bold">
                                                    Delivery Profile
                                                </h5>
                                                <span className="badge bg-info mb-2" style={{ fontSize: '0.75rem' }}>
                                                    Product-Area Link
                                                </span>
                                            </div>
                                            <div className="btn-group">
                                                <Link 
                                                    to={`/inventory/v1.2/delivery-profiles/${profile._id}`}
                                                    className="btn btn-sm btn-outline-primary"
                                                    title="Edit Profile"
                                                >
                                                    <Edit size={14} />
                                                </Link>
                                                <button 
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteClick(profile)}
                                                    title="Delete Profile"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body pt-2">
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <div className="d-flex align-items-center mb-2">
                                                    <Package size={16} className="text-success me-2" />
                                                    <div>
                                                        <div className="small text-muted">Product Profile</div>
                                                        <div className="small fw-bold text-truncate">
                                                            {profile.productProfile || 'Not specified'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <div className="d-flex align-items-center mb-2">
                                                    <MapPin size={16} className="text-warning me-2" />
                                                    <div>
                                                        <div className="small text-muted">
                                                            {profile.superServiceableArea ? 'Super Serviceable Area' : 'Serviceable Area'}
                                                        </div>
                                                        <div className="small fw-bold text-truncate">
                                                            {profile.serviceableArea || profile.superServiceableArea || 'Not specified'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Timestamps */}
                                        <div className="mt-3 pt-3 border-top">
                                            <div className="row">
                                                <div className="col-6">
                                                    <div className="small text-muted">Created</div>
                                                    <div className="small">
                                                        {profile.createdAt ? formatDate(profile.createdAt) : 'N/A'}
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="small text-muted">Updated</div>
                                                    <div className="small">
                                                        {profile.updatedAt ? formatDate(profile.updatedAt) : 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-light border-top-0">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Link 
                                                to={`/inventory/v1.2/delivery-profiles/${profile._id}`}
                                                className="btn btn-primary btn-sm"
                                            >
                                                <Edit size={14} className="me-1" />
                                                Manage
                                            </Link>
                                            <small className="text-muted">
                                                ID: {profile._id?.slice(-6)}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <Truck size={64} className="text-muted" />
                        </div>
                        <h4 className="text-muted mb-3">
                            {searchTerm ? 'No profiles found' : 'No delivery profiles created yet'}
                        </h4>
                        <p className="text-muted mb-4">
                            {searchTerm 
                                ? `No profiles match "${searchTerm}". Try adjusting your search.`
                                : 'Get started by creating your first delivery profile.'
                            }
                        </p>
                        {!searchTerm && (
                            <Link 
                                to="/inventory/v1.2/delivery-profiles/new"
                                className="btn btn-primary btn-lg"
                            >
                                <Plus size={20} className="me-2" />
                                Create Your First Profile
                            </Link>
                        )}
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title d-flex align-items-center text-danger">
                                        <AlertTriangle size={24} className="me-2" />
                                        Confirm Delete
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={cancelDelete}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="text-center">
                                        <div className="mb-3">
                                            <Trash2 size={48} className="text-danger" />
                                        </div>
                                        <h6 className="mb-3">Delete Delivery Profile?</h6>
                                        <p className="text-muted mb-4">
                                            This action cannot be undone. The link between the product profile 
                                            and serviceable area will be permanently removed.
                                        </p>
                                        <div className="alert alert-warning d-flex align-items-center">
                                            <AlertTriangle size={20} className="me-2" />
                                            <small>
                                                <strong>Warning:</strong> This will permanently delete the delivery profile.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={cancelDelete}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={confirmDelete}
                                    >
                                        <Trash2 size={16} className="me-2" />
                                        Delete Profile
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};

DeliveryProfiles.propTypes = {
    getDeliveryProfiles: PropTypes.func.isRequired,
    deleteDeliveryProfile: PropTypes.func.isRequired,
    setAlert: PropTypes.func.isRequired,
    deliveryProfiles: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
    deliveryProfiles: state.deliveryProfile.deliveryProfiles,
    loading: state.deliveryProfile.loading
});

export default connect(mapStateToProps, { getDeliveryProfiles, deleteDeliveryProfile, setAlert })(DeliveryProfiles);
